import React, { useState, useEffect } from "react";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../../../../core/data/redux/apis/urls";
import { formatDate } from "./common/nocUtils";
import { handleDocumentClick } from "./common/documentUtils";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocDandARCard } from "./common/NocDandARCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";
import { DnarOption } from "./common/types";

interface NocExIndiaApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  dnarMasterList: DnarOption[];
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocExIndiaApproval: React.FC<NocExIndiaApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  dnarMasterList,
  onShowToast,
}) => {
  // Editable fields
  const [placeOfVisitNoc, setPlaceOfVisitNoc] = useState<string>("");
  const [purposeOfVisit, setPurposeOfVisit] = useState<string>("");
  const [periodOfVisitFrom, setPeriodOfVisitFrom] = useState<string>("");
  const [periodOfVisitTo, setPeriodOfVisitTo] = useState<string>("");
  const [leaveDetails, setLeaveDetails] = useState<string>("");
  const [department, setDepartment] = useState<string>("");

  // D&AR State
  const [dnARStatus, setDnARStatus] = useState<string>("");
  const [dnARPenaltyEffective, setDnARPenaltyEffective] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");

  // Approver Remarks (HRemarks)
  const [hRemarks, setHRemarks] = useState<string>("");

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  const getFieldStatus = (fieldName: string): "EDITABLE" | "READONLY" | "HIDDEN" => {
    const rawVal =
      nocData?.enabled_fields_fields_json?.[fieldName] ||
      nocData?.enabled_fields_json?.[fieldName] ||
      nocData?.[fieldName];

    if (rawVal === "X" || rawVal === "V" || rawVal === true) return "EDITABLE";
    return "READONLY";
  };

  useEffect(() => {
    if (nocData) {
      setPlaceOfVisitNoc(nocData?.placeOfVisitNoc || nocData?.placeOfVisit || "");
      setPurposeOfVisit(nocData?.purposeOfVisit || "");
      setPeriodOfVisitFrom(nocData?.periodFrom || nocData?.periodOfVisitFrom || "");
      setPeriodOfVisitTo(nocData?.periodTo || nocData?.periodOfVisitTo || "");
      setLeaveDetails(nocData?.leaveDetails || "");
      setDepartment(
        nocData?.department ||
        nocData?.dept ||
        nocData?.departmentName ||
        nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
        ""
      );
      setDnARStatus(
        getFieldStatus("DandARStatus") === "EDITABLE"
          ? (nocData?.DandARStatus || "")
          : (nocData?.DandARStatus || nocData?.dnARStatus || "")
      );
      setDnARPenaltyEffective(nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || nocData?.dandARPenaltyEffective || "");
      const isRemarksEditable = getFieldStatus("Remarks") === "EDITABLE";
      const initialRemarks = isRemarksEditable
        ? ""
        : ((nocData?.Remarks !== "X" && nocData?.Remarks) ||
           (nocData?.remarks !== "X" && nocData?.remarks) ||
           "");
      setRemarks(initialRemarks);
      setHRemarks("");
    }
  }, [nocData]);

  const handleApproveReject = async (action: "A" | "R") => {
    // Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    if (action === "A" && getFieldStatus("DandARStatus") === "EDITABLE" && !dnARStatus) {
      onShowToast("Error", "D&AR status is required before approving.", "danger");
      return;
    }

    if (action === "A" && (dnARStatus === "M" || dnARStatus === "J") && !dnARPenaltyEffective) {
      onShowToast("Error", "D&AR Penalty Effective Date is required for Minor/Major penalty.", "danger");
      return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const isRemarksFieldEditable = getFieldStatus("Remarks") === "EDITABLE";
      const payload = {
        Approve_Reject_flag: action,
        ReferenceNumber: nocData.referenceNumber,
        HRemarks: (hRemarks || "").trim(),
        Remarks: (isRemarksFieldEditable ? remarks : (nocData?.Remarks || nocData?.remarks || remarks || "")).trim(),
        DnARStatus: dnARStatus || nocData?.DandARStatus || nocData?.dnARStatus || "",
        DnARPenaltyEffecti: dnARPenaltyEffective || nocData?.dnARPenaltyEffecti || nocData?.DnARPenaltyEffecti || "",
        placeOfVisitNoc: placeOfVisitNoc || nocData?.placeOfVisit || nocData?.placeOfVisitNoc || "",
        purposeOfVisit: purposeOfVisit || nocData?.purposeOfVisit || "",
        periodOfVisitFrom: periodOfVisitFrom || nocData?.periodFrom || nocData?.periodOfVisitFrom || "",
        periodOfVisitTo: periodOfVisitTo || nocData?.periodTo || nocData?.periodOfVisitTo || "",
        leaveDetails: leaveDetails || nocData?.leaveDetails || "",
        department: department || nocData?.department || "",
      };

      console.log("Ex-India Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc-ex-india/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Ex-India NOC application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Ex-India Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const isPlaceEditable =
    nocData?.enabled_fields_fields_json?.PlaceOfVisitNOC === "X" ||
    nocData?.enabled_fields_json?.PlaceOfVisitNOC === "X";

  const isPurposeEditable =
    nocData?.enabled_fields_fields_json?.PurposeOfVisit === "X" ||
    nocData?.enabled_fields_json?.PurposeOfVisit === "X";

  const isFromDateEditable =
    nocData?.enabled_fields_fields_json?.PeriodOfVisitFrom === "X" ||
    nocData?.enabled_fields_json?.PeriodOfVisitFrom === "X";

  const isToDateEditable =
    nocData?.enabled_fields_fields_json?.PeriodOfVisitTo === "X" ||
    nocData?.enabled_fields_json?.PeriodOfVisitTo === "X";

  const isLeaveEditable =
    nocData?.enabled_fields_fields_json?.LeaveDetails === "X" ||
    nocData?.enabled_fields_json?.LeaveDetails === "X";

  const travelBorneText =
    nocData?.noc_travel?.travel_title ||
    nocData?.tExpenditureBorneByDetails?.travel_title ||
    (nocData?.tExpenditureBorneBy === "E"
      ? "Self"
      : nocData?.tExpenditureBorneBy === "S"
        ? "Spouse"
        : nocData?.tExpenditureBorneBy === "O"
          ? "Others"
          : nocData?.tExpenditureBorneBy) ||
    "N/A";

  const stayBorneText =
    nocData?.stay_list?.stay_title ||
    nocData?.sExpenditureBorneByDetails?.stay_title ||
    (nocData?.sExpenditureBorneBy === "E"
      ? "Self"
      : nocData?.sExpenditureBorneBy === "S"
        ? "Spouse"
        : nocData?.sExpenditureBorneBy === "O"
          ? "Others"
          : nocData?.sExpenditureBorneBy === "H"
            ? "Hotel"
            : nocData?.sExpenditureBorneBy) ||
    "N/A";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="NOC for Ex-India Leave / Visit Approval"
          breadcrumbTitle="Ex-India Request Details"
          nocType="NOC_EXINDIA"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Info */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_EXINDIA"
          editableDepartment={department}
          onDepartmentChange={setDepartment}
        />

        {/* Ex-India Details Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-plane me-2 text-primary"></i>
              Ex-India Leave & Travel Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {/* Place of Visit */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Place of Visit
                </label>
                {isPlaceEditable ? (
                  <input
                    type="text"
                    className="form-control"
                    value={placeOfVisitNoc}
                    onChange={(e) => setPlaceOfVisitNoc(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{placeOfVisitNoc || "N/A"}</div>
                )}
              </div>

              {/* Purpose of Visit */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Purpose of Visit
                </label>
                {isPurposeEditable ? (
                  <input
                    type="text"
                    className="form-control"
                    value={purposeOfVisit}
                    onChange={(e) => setPurposeOfVisit(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{purposeOfVisit || "N/A"}</div>
                )}
              </div>

              {/* Period From */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Period of Visit From
                </label>
                {isFromDateEditable ? (
                  <input
                    type="date"
                    className="form-control"
                    value={periodOfVisitFrom}
                    onChange={(e) => setPeriodOfVisitFrom(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{formatDate(periodOfVisitFrom)}</div>
                )}
              </div>

              {/* Period To */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Period of Visit To
                </label>
                {isToDateEditable ? (
                  <input
                    type="date"
                    className="form-control"
                    value={periodOfVisitTo}
                    onChange={(e) => setPeriodOfVisitTo(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{formatDate(periodOfVisitTo)}</div>
                )}
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Total Members Travelling
                </label>
                <div className="fw-bold text-dark">{nocData?.totalMembersTravel || "N/A"}</div>
              </div>

              {/* Leave Details */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Period of Leave (From - To)
                </label>
                {isLeaveEditable ? (
                  <input
                    type="text"
                    className="form-control"
                    value={leaveDetails}
                    onChange={(e) => setLeaveDetails(e.target.value)}
                  />
                ) : (
                  <div className="fw-bold text-dark">{leaveDetails || "N/A"}</div>
                )}
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Staying Expenditure Amount
                </label>
                <div className="fw-bold text-dark">
                  ₹ {Number(nocData?.stayingExpenditure || 0).toLocaleString("en-IN")}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Travel Expenditure Amount
                </label>
                <div className="fw-bold text-dark">
                  ₹ {Number(nocData?.travelExpenditureA || 0).toLocaleString("en-IN")}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Other Expenses
                </label>
                <div className="fw-bold text-dark">
                  ₹ {Number(nocData?.otherExpenditureAm || 0).toLocaleString("en-IN")}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Total Expenditure
                </label>
                <div className="fw-bold text-primary">
                  ₹ {Number(nocData?.totalExpenditure || 0).toLocaleString("en-IN")}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Expenditure Borne For Travel
                </label>
                <div className="fw-bold text-dark">
                  {travelBorneText}
                  {nocData?.tRelativesName ? ` (${nocData.tRelativesName} - ${nocData.tRelationWithPerson || ""})` : ""}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Expenditure Borne For Staying
                </label>
                <div className="fw-bold text-dark">
                  {stayBorneText}
                  {nocData?.sRelativesName ? ` (${nocData.sRelativesName} - ${nocData.sRelationWithPerson || ""})` : ""}
                </div>
              </div>

              {/* Travel Entries Itinerary Table */}
              {Array.isArray(nocData?.travelEntries) && nocData.travelEntries.length > 0 && (
                <div className="col-12 mt-3">
                  <label className="form-label text-muted mb-2 text-uppercase small fw-semibold">
                    Travel Itinerary & Countries Visited
                  </label>
                  <div className="table-responsive border rounded">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Seq</th>
                          <th>Country</th>
                          <th>Duration of Visit</th>
                          <th>Purpose of Visit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nocData.travelEntries.map((tItem: any, idx: number) => (
                          <tr key={idx}>
                            <td>{tItem.seq || idx + 1}</td>
                            <td>{tItem.country || "-"}</td>
                            <td>{tItem.durationOfVisit || "-"}</td>
                            <td>{tItem.purposeOfVisit || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Attachments */}
        {Array.isArray(nocData?.documents) && nocData.documents.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-paperclip me-2 text-primary"></i>
                Application Attachments
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {nocData.documents.map((doc: any, idx: number) => (
                  <button
                    key={doc.id || idx}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center"
                    onClick={() => handleDocumentClick(doc, "NOC_EXINDIA", onShowToast)}
                  >
                    <i className="ti ti-file me-1"></i>
                    {doc.fileName?.split("/").pop() || `Attachment ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Work History */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* D&AR Status */}
        <NocDandARCard
          nocData={nocData}
          nocType="NOC_EXINDIA"
          dnARStatus={dnARStatus}
          dnARPenaltyEffective={dnARPenaltyEffective}
          remarks={remarks}
          dnarMasterList={dnarMasterList}
          onStatusChange={setDnARStatus}
          onPenaltyEffectiveChange={setDnARPenaltyEffective}
          onRemarksChange={setRemarks}
          getFieldStatus={getFieldStatus}
        />

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_EXINDIA"
          onShowToast={onShowToast}
        />

        {/* Actions Modal */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
